import {
  createContext, useContext, useState, useCallback, useRef, useEffect, useMemo,
  type ReactNode,
} from 'react';
import Decimal from 'decimal.js';
import type { Transaction } from '../types';
import { assertFirmId } from '../utils/assertFirmId';
import type { RegistroCompra } from '../services/SunatService';
import type { SerializedJournalEntry } from '../services/AccountingEngine';
import { useCompanyContext } from './CompanyContext';
import { createRepositories } from '../repositories';
import { IGV_DIVISOR } from '../constants/limits';

// ── Tipo RCE extendido ──────────────────────────────────────────
export type RceRow = RegistroCompra & { validado: boolean };

// ── Datos iniciales del RCE (8 comprobantes con RUCs peruanos) ──
export const INITIAL_RCE_ROWS: RceRow[] = [
  {
    periodo: '202501', cuo: '202501-00001', correlativo: 'M0001',
    fechaEmision: '05/01/2025', fechaVcto: '05/01/2025',
    tipoComprobante: '01', serie: 'F001', numero: '00125430',
    tipoDocProveedor: '6', rucProveedor: '20100017491',
    razonSocial: 'TELEFÓNICA DEL PERÚ S.A.A.',
    baseImponible: new Decimal('847.46'), igv: new Decimal('152.54'),
    noGravado: new Decimal('0.00'), total: new Decimal('1000.00'),
    moneda: 'PEN', tipoCambio: new Decimal('1.000'), estado: '1', validado: true,
  },
  {
    periodo: '202501', cuo: '202501-00002', correlativo: 'M0002',
    fechaEmision: '07/01/2025', fechaVcto: '07/01/2025',
    tipoComprobante: '01', serie: 'F001', numero: '00089234',
    tipoDocProveedor: '6', rucProveedor: '20504948901',
    razonSocial: 'ENTEL PERÚ S.A.',
    baseImponible: new Decimal('127.12'), igv: new Decimal('22.88'),
    noGravado: new Decimal('0.00'), total: new Decimal('150.00'),
    moneda: 'PEN', tipoCambio: new Decimal('1.000'), estado: '1', validado: true,
  },
  {
    periodo: '202501', cuo: '202501-00003', correlativo: 'M0003',
    fechaEmision: '10/01/2025', fechaVcto: '10/01/2025',
    tipoComprobante: '14', serie: '0001', numero: '00045123',
    tipoDocProveedor: '6', rucProveedor: '20336540593',
    razonSocial: 'LUZ DEL SUR S.A.A.',
    baseImponible: new Decimal('356.78'), igv: new Decimal('64.22'),
    noGravado: new Decimal('0.00'), total: new Decimal('421.00'),
    moneda: 'PEN', tipoCambio: new Decimal('1.000'), estado: '1', validado: true,
  },
  {
    periodo: '202501', cuo: '202501-00004', correlativo: 'M0004',
    fechaEmision: '15/01/2025', fechaVcto: '15/01/2025',
    tipoComprobante: '01', serie: 'F001', numero: '00234567',
    tipoDocProveedor: '6', rucProveedor: '20321730895',
    razonSocial: 'RIMAC SEGUROS Y REASEGUROS',
    baseImponible: new Decimal('1271.19'), igv: new Decimal('228.81'),
    noGravado: new Decimal('0.00'), total: new Decimal('1500.00'),
    moneda: 'PEN', tipoCambio: new Decimal('1.000'), estado: '1', validado: true,
  },
  {
    periodo: '202501', cuo: '202501-00005', correlativo: 'M0005',
    fechaEmision: '20/01/2025', fechaVcto: '20/01/2025',
    tipoComprobante: '01', serie: 'F001', numero: '00123456',
    tipoDocProveedor: '6', rucProveedor: '20419026809',
    razonSocial: 'SODIMAC PERÚ S.A.',
    baseImponible: new Decimal('847.46'), igv: new Decimal('152.54'),
    noGravado: new Decimal('0.00'), total: new Decimal('1000.00'),
    moneda: 'PEN', tipoCambio: new Decimal('1.000'), estado: '1', validado: true,
  },
  {
    periodo: '202501', cuo: '202501-00006', correlativo: 'M0006',
    fechaEmision: '22/01/2025', fechaVcto: '22/01/2025',
    tipoComprobante: '91', serie: '0000', numero: '00000001',
    tipoDocProveedor: '0', rucProveedor: '00000000000',
    razonSocial: 'GOOGLE LLC',
    baseImponible: new Decimal('1016.95'), igv: new Decimal('183.05'),
    noGravado: new Decimal('0.00'), total: new Decimal('1200.00'),
    moneda: 'PEN', tipoCambio: new Decimal('1.000'), estado: '1', validado: false,
  },
  {
    periodo: '202501', cuo: '202501-00007', correlativo: 'M0007',
    fechaEmision: '24/01/2025', fechaVcto: '24/01/2025',
    tipoComprobante: '01', serie: 'F001', numero: '00078901',
    tipoDocProveedor: '6', rucProveedor: '20346477729',
    razonSocial: 'TAI LOY S.A.',
    baseImponible: new Decimal('550.85'), igv: new Decimal('99.15'),
    noGravado: new Decimal('0.00'), total: new Decimal('650.00'),
    moneda: 'PEN', tipoCambio: new Decimal('1.000'), estado: '1', validado: false,
  },
  {
    periodo: '202501', cuo: '202501-00008', correlativo: 'M0008',
    fechaEmision: '28/01/2025', fechaVcto: '28/01/2025',
    tipoComprobante: '01', serie: 'F001', numero: '00456789',
    tipoDocProveedor: '6', rucProveedor: '20100055237',
    razonSocial: 'ALICORP S.A.A.',
    baseImponible: new Decimal('1949.15'), igv: new Decimal('350.85'),
    noGravado: new Decimal('0.00'), total: new Decimal('2300.00'),
    moneda: 'PEN', tipoCambio: new Decimal('1.000'), estado: '1', validado: false,
  },
];

// ── Generadores de datos de simulación ──────────────────────────
function makeSimTx(n: number): Transaction {
  const total = 590 + n * 100;
  const bi  = new Decimal(total).div(IGV_DIVISOR).toDecimalPlaces(2);
  const igv = new Decimal(total).minus(bi).toDecimalPlaces(2);
  const now = new Date();
  return {
    id: 9000 + n,
    date: now.toISOString().split('T')[0],
    description: `Factura Simulada #${n} — PROVEEDOR TEST S.A.C.`,
    amount: -total,
    cuenta: '6310',
    nombreCuenta: 'Transporte de Bienes',
    status: 'auto' as const,
    confidence: 97,
    baseImponible: bi.toNumber(),
    igv: igv.toNumber(),
  };
}


// ── Context interface ────────────────────────────────────────────
interface SyncContextValue {
  liveTransactions: Transaction[];
  setLiveTransactions: (txs: Transaction[]) => void;
  liveRceRows: RceRow[];
  /** Asientos contables de partida doble — reactivos a Supabase */
  liveJournalEntries: SerializedJournalEntry[];
  /** Asientos contables YTD (enero→mes activo) — NIC 1 Estado de Resultados */
  ytdJournalEntries: SerializedJournalEntry[];
  /** Transacciones YTD (enero→mes activo) — NIC 7 Flujo de Efectivo */
  ytdTransactions: Transaction[];
  lastSync: Date | null;
  syncCount: number;
  handleUploadSimulation: () => void;
  /** Trigger a manual re-fetch of transactions and journal entries */
  triggerRefresh: () => void;
  /**
   * UX-2 (audit Lenny 2026-07-06): true mientras el fetch inicial está en
   * vuelo SIN cache que mostrar. Los consumidores deben renderizar skeleton
   * o "—" en vez de montos — nunca S/ 0.00 como si fuera dato real.
   * Con cache (stale-while-revalidate) es false: hay data legítima visible.
   */
  isLoading: boolean;
}

const SyncContext = createContext<SyncContextValue | null>(null);

// Module-level cache — survives component unmounts (route changes).
// cacheRef inside the component resets to {} on every mount, causing constant MISS.
const _cache: Record<string, {
  transactions: Transaction[];
  journalEntries: SerializedJournalEntry[];
  ytdTransactions: Transaction[];
  ytdJournalEntries: SerializedJournalEntry[];
  timestamp: number;
}> = {};

export function useSyncContext(): SyncContextValue {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSyncContext must be used within <SyncProvider>');
  return ctx;
}

// ── Provider ────────────────────────────────────────────────────
export function SyncProvider({ children }: { children: ReactNode }) {
  const { activeCompanyId, activeFirmId, filterPeriodo } = useCompanyContext();

  const [liveTransactions, setLiveTransactionsState] = useState<Transaction[]>([]);
  const [liveJournalEntries, setLiveJournalEntries] = useState<SerializedJournalEntry[]>([]);
  const [ytdJournalEntries, setYtdJournalEntries]   = useState<SerializedJournalEntry[]>([]);
  const [ytdTransactions,   setYtdTransactions]     = useState<Transaction[]>([]);
  const [lastSync, setLastSync]                     = useState<Date | null>(null);
  const [syncCount, setSyncCount]                   = useState(0);
  const [refreshKey, setRefreshKey]                 = useState(0);
  // Arranca true: evita el flash de S/ 0.00 antes de que corra el primer effect
  const [isLoading, setIsLoading]                   = useState(true);
  const simRef = useRef(0);

  const repos = useMemo(() => createRepositories(), []);

  // Regla de Oro #1 — Privacidad Absoluta: la data contable solo vive en
  // Supabase (Cloud-First). localStorage queda reservado EXCLUSIVAMENTE
  // para metadata de sesión (activeCompanyId, modo Contador/Emprendedor, etc.).
  const setLiveTransactions = useCallback((txs: Transaction[]) => {
    setLiveTransactionsState(txs);
  }, []);

  // liveRceRows — derivado de liveTransactions (compras, amount < 0).
  // Reemplaza el mock estático INITIAL_RCE_ROWS: ahora refleja exactamente
  // lo que está en Supabase para el período activo.
  const liveRceRows = useMemo<RceRow[]>(() => {
    const periodoKey = filterPeriodo.replace('-', '');
    return liveTransactions
      .filter((tx) => (tx.amount ?? 0) < 0)
      .map((tx, idx) => {
        const abs = new Decimal(tx.amount ?? 0).abs();
        const bi  = tx.baseImponible != null
          ? new Decimal(tx.baseImponible)
          : abs.div(IGV_DIVISOR).toDecimalPlaces(2);
        const igv = tx.igv != null
          ? new Decimal(tx.igv)
          : abs.minus(bi).toDecimalPlaces(2);
        const txAny = tx as unknown as Record<string, unknown>;
        const pKey  = (txAny['periodo'] as string | undefined)?.replace('-', '') ?? periodoKey;
        const rawId = (txAny['dbId'] as number | undefined) ?? tx.id ?? (idx + 1);
        const fecha = tx.date?.split('-').reverse().join('/') ?? '';
        return {
          periodo:          pKey,
          cuo:              `${pKey}-${String(rawId).padStart(5, '0')}`,
          correlativo:      tx.correlativo ?? `M${String(idx + 1).padStart(4, '0')}`,
          fechaEmision:     fecha,
          fechaVcto:        fecha,
          tipoComprobante:  tx.tipoDocumento ?? '01',
          serie:            tx.serie ?? '',
          numero:           tx.correlativo ?? '',
          tipoDocProveedor: '6',
          rucProveedor:     (txAny['rucProveedor'] as string | undefined) ?? '',
          razonSocial:      (txAny['razonSocialProveedor'] as string | undefined) ?? tx.description,
          baseImponible:    bi.abs(),
          igv:              igv.abs(),
          noGravado:        new Decimal('0.00'),
          total:            new Decimal(abs),
          moneda:           (txAny['moneda'] as string | undefined) ?? 'PEN',
          tipoCambio:       new Decimal('1.000'),
          estado:           tx.anulada ? '8' : '1',
          validado:         tx.status === 'approved',
        } as RceRow;
      });
  }, [liveTransactions, filterPeriodo]);

  /** Force a re-fetch of transactions and journal entries from Supabase + Dexie */
  const triggerRefresh = useCallback(() => {
    // Clear the full module-level cache so the effect fetches fresh data
    // without serving stale entries first (BUG-012: uploaded XML invisible
    // until manual refresh due to stale-while-revalidate serving old data).
    Object.keys(_cache).forEach(key => delete _cache[key]);
    setRefreshKey(k => k + 1);   // → re-fetch SyncContext liveTransactions from Supabase
    setSyncCount(c => c + 1);    // → re-fetch Compras/Ventas from Dexie
  }, []);

  // ── Supabase fetch: stale-while-revalidate ──────────────────────
  const CACHE_TTL = 300_000; // 5 minutos
  useEffect(() => {
    const cid    = activeCompanyId;
    const period = filterPeriodo;
    if (!cid || !period || !activeFirmId) {
      setLiveTransactionsState([]);
      setLiveJournalEntries([]);
      setYtdJournalEntries([]);
      setYtdTransactions([]);
      setIsLoading(false); // sin empresa/período no hay nada que cargar
      return;
    }
    const fid = assertFirmId(activeFirmId, 'SyncContext.fetchSupabase');

    const key    = `${fid}-${cid}-${period}`;
    const cached = _cache[key];
    const now    = Date.now();

    // Serve stale data immediately — elimina spinner en cambios de período/empresa ya visitados
    if (cached) {
      setLiveTransactionsState(cached.transactions);
      setLiveJournalEntries(cached.journalEntries);
      setYtdTransactions(cached.ytdTransactions);
      setYtdJournalEntries(cached.ytdJournalEntries);
      setIsLoading(false); // hay data legítima en pantalla (aunque se revalide detrás)
    } else {
      setIsLoading(true);  // cold load: skeleton hasta que llegue el fetch
    }

    // Skip network if cache is fresh AND no manual refresh was requested
    // refreshKey > 0 means triggerRefresh() was called explicitly — always fetch
    const isFresh = cached && (now - cached.timestamp) < CACHE_TTL;
    if (isFresh && refreshKey === 0) {
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    Promise.all([
      repos.transactions.getByPeriod(fid, cid, period),
      repos.journalEntries.getByPeriod(fid, cid, period),
      repos.transactions.getByPeriodYTD(fid, cid, period),
      repos.journalEntries.getByPeriodYTD(fid, cid, period),
    ])
      .then(([txs, entries, ytdTxs, ytdEntries]) => {
        if (cancelled) return;
        const t    = txs as Transaction[];
        const e    = entries as SerializedJournalEntry[];
        const ytdT = ytdTxs as Transaction[];
        const ytdE = ytdEntries as SerializedJournalEntry[];
        setLiveTransactionsState(t);
        setLiveJournalEntries(e);
        setYtdTransactions(ytdT);
        setYtdJournalEntries(ytdE);
        _cache[key] = { transactions: t, journalEntries: e, ytdTransactions: ytdT, ytdJournalEntries: ytdE, timestamp: Date.now() };
        setIsLoading(false);
        // Reset refreshKey so subsequent renders use cache normally
        if (refreshKey > 0) setRefreshKey(0);
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        setIsLoading(false); // error de red: soltar el skeleton (el warn queda en consola)
        console.warn('[SyncContext] fetch failed:', err);
      });
    return () => { cancelled = true; controller.abort(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompanyId, activeFirmId, filterPeriodo, refreshKey]);

  const handleUploadSimulation = useCallback(() => {
    simRef.current += 1;
    const n  = simRef.current;
    const tx = makeSimTx(n);
    // liveRceRows se deriva automáticamente de liveTransactions — no necesita update manual
    setLiveTransactionsState((prev) => [...prev, tx]);
    setLastSync(new Date());
    setSyncCount((c) => c + 1);
  }, []);

  return (
    <SyncContext.Provider value={{
      liveTransactions,
      setLiveTransactions,
      liveRceRows,
      liveJournalEntries,
      ytdJournalEntries,
      ytdTransactions,
      lastSync,
      syncCount,
      handleUploadSimulation,
      triggerRefresh,
      isLoading,
    }}>
      {children}
    </SyncContext.Provider>
  );
}
